// components/landing/useTurnstile.ts
// Cloudflare Turnstile as a React hook — explicit render into a container ref,
// one shared script tag, widget removed on unmount, reset() for a fresh token,
// and an `enabled` option that defers the script and widget until true.

import { useCallback, useEffect, useRef, useState } from 'react'
import type { RefObject } from 'react'

const TURNSTILE_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

export interface TurnstileRenderOptions {
  sitekey: string
  theme?: 'auto' | 'light' | 'dark'
  size?: 'normal' | 'flexible' | 'compact'
  callback?: (token: string) => void
  'expired-callback'?: () => void
  'error-callback'?: () => void
}

export interface TurnstileApi {
  render: (
    container: HTMLElement,
    options: TurnstileRenderOptions,
  ) => string | undefined
  reset: (widgetId?: string) => void
  remove: (widgetId?: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

export type TurnstileStatus = 'loading' | 'ready' | 'error'

export interface UseTurnstileResult {
  containerRef: RefObject<HTMLDivElement | null>
  /** Latest token, or '' before the check passes and after it expires or is reset. */
  token: string
  /** 'error' when the script or the widget could not load. */
  status: TurnstileStatus
  /** Ask Turnstile for a new token (a token is single-use). */
  reset: () => void
}

/** Load api.js once. A tag already in the document is reused, never duplicated. */
function loadTurnstile(): Promise<TurnstileApi> {
  return new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve(window.turnstile)
      return
    }

    let script = document.querySelector<HTMLScriptElement>(
      `script[src="${TURNSTILE_SRC}"]`,
    )
    if (!script) {
      script = document.createElement('script')
      script.src = TURNSTILE_SRC
      script.async = true
      script.defer = true
      document.head.appendChild(script)
    }

    const tag = script
    tag.addEventListener(
      'load',
      () => {
        if (window.turnstile) resolve(window.turnstile)
        else reject(new Error('Turnstile did not initialise'))
      },
      { once: true },
    )
    tag.addEventListener(
      'error',
      () => {
        // Drop the dead tag so a remount can try again.
        tag.remove()
        reject(new Error('Turnstile script failed to load'))
      },
      { once: true },
    )
  })
}

export interface UseTurnstileOptions {
  /** While false the script is not loaded and no widget renders. Defaults to true. */
  enabled?: boolean
}

export function useTurnstile(
  siteKey: string,
  { enabled = true }: UseTurnstileOptions = {},
): UseTurnstileResult {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const widgetIdRef = useRef<string | undefined>(undefined)
  const [token, setToken] = useState('')
  const [status, setStatus] = useState<TurnstileStatus>('loading')

  useEffect(() => {
    const container = containerRef.current
    if (!enabled || !container) return

    let cancelled = false

    loadTurnstile()
      .then((api) => {
        if (cancelled) return
        const widgetId = api.render(container, {
          sitekey: siteKey,
          theme: 'dark',
          size: 'flexible',
          callback: (value) => {
            setToken(value)
            setStatus('ready')
          },
          'expired-callback': () => setToken(''),
          'error-callback': () => {
            setToken('')
            setStatus('error')
          },
        })
        if (widgetId === undefined) {
          setStatus('error')
          return
        }
        widgetIdRef.current = widgetId
        setStatus((current) => (current === 'loading' ? 'ready' : current))
      })
      .catch(() => {
        if (!cancelled) setStatus('error')
      })

    return () => {
      cancelled = true
      const widgetId = widgetIdRef.current
      widgetIdRef.current = undefined
      if (widgetId !== undefined) {
        try {
          window.turnstile?.remove(widgetId)
        } catch {
          // The widget is already gone; nothing left to clean up.
        }
      }
    }
  }, [siteKey, enabled])

  const reset = useCallback(() => {
    setToken('')
    const widgetId = widgetIdRef.current
    if (widgetId === undefined) return
    try {
      window.turnstile?.reset(widgetId)
    } catch {
      setStatus('error')
    }
  }, [])

  return { containerRef, token, status, reset }
}
